/**
 * `<RequireDisplayName/>` — first-run display-name gate.
 *
 * Issue 010 (P1) / Brief 37 chunk 2 — closes Layer B's Tarii-side gap
 * independently of the Clerk dashboard work (founder action F6). Every
 * new account currently lands the cascade-step-6 fallback shape
 * `User <id-prefix>` (Brief 17 / `c63e3a9` cascade in
 * `backend/src/auth/middleware.py::_derive_display_name`) because Clerk's
 * default sign-up flow doesn't capture a name. This gate detects the
 * fallback and redirects new sign-ins to `/settings/profile?onboarding=1`
 * with a calm welcome hero before they reach `/teams/...`.
 *
 * Mirrors the `<MemberWelcomeGate/>` shape from Round J / `fa36af0`:
 * wrapper component sitting between `<RequireAuthOrLanding/>` and
 * `<AppShell/>`, short-circuiting via `<Outlet/>` when no redirect is
 * needed and via `<Navigate replace/>` when one is.
 *
 * Bypass conditions (in order):
 *   1. Loading state (no account yet) → render outlet. Don't gate
 *      during the initial fetch — a redirect-on-loading would race
 *      AuthContext hydration.
 *   2. Account fetch error → render outlet. Don't block on error;
 *      the user can still reach `/settings/profile` via the user-pill
 *      menu and edit later.
 *   3. localStorage `tarii.display_name.dismissed.<accountId> === "1"`
 *      → bypass. Set when the user saves a name on the onboarding
 *      variant of `/settings/profile` OR clicks "Skip for now". One
 *      bounce per browser per account is enough — the onboarding
 *      surface is opt-in repeat-visitable via direct URL.
 *   4. Current path matches `/settings/profile` → bypass (avoid the
 *      redirect loop; the destination IS the profile page).
 *   5. `isDisplayNameFallback(account.display_name)` is FALSE → bypass
 *      (the user has a real name; nothing to prompt for).
 *
 * Otherwise: redirect to `/settings/profile?onboarding=1` (the
 * onboarding-variant of the Brief 29 form).
 *
 * Why account-scoped (not role-scoped): unlike `<MemberWelcomeGate/>`
 * which only fires for members (leads have `OnboardingPage`), the
 * display-name fallback hits leads AND members. A founder who signs
 * up via the Clerk hosted flow lands `User 3D9mZIe8` just as a
 * worker accepting an invite does. Both cohorts deserve the gate.
 *
 * Why not a useEffect inside AppShell: same reasoning as
 * `<MemberWelcomeGate/>`. Keeping the redirect at the route-tree
 * level means the user never paints the AppShell on first entry —
 * they go straight from RequireAuth → /settings/profile. No race
 * with AppShell mount + chrome paint.
 */
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "../lib/AuthContext";
import { isDisplayNameFallback } from "../lib/displayNameFallback";

/**
 * Shared with the `?onboarding=1` save / skip handlers in
 * `routes/settings/ProfilePage.tsx`. Same key shape as the Round J
 * privacy-interstitial dismissal flag (`tarii.privacy.three_circles.dismissed.<id>`)
 * so the localStorage namespace stays predictable.
 */
export const DISPLAY_NAME_DISMISSED_KEY_PREFIX = "tarii.display_name.dismissed.";

/**
 * Pure helper — exported so the gate AND the ProfilePage onboarding
 * variant can both write/read the same flag. Privacy-mode browsers
 * throw on `localStorage.getItem`; we treat that as "not dismissed"
 * so the gate still fires (the worse failure mode is a re-prompt,
 * not a missed prompt).
 */
export function readDisplayNameDismissed(accountId: string | null): boolean {
  if (!accountId) return false;
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(`${DISPLAY_NAME_DISMISSED_KEY_PREFIX}${accountId}`) === "1";
  } catch {
    return false;
  }
}

export function writeDisplayNameDismissed(accountId: string | null): void {
  if (!accountId) return;
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${DISPLAY_NAME_DISMISSED_KEY_PREFIX}${accountId}`, "1");
  } catch {
    // Best-effort persistence; the in-memory navigation still completes.
  }
}

export function RequireDisplayName() {
  const { account, accountId, loading, error } = useAuth();
  const location = useLocation();

  // 1. Loading — don't gate during the initial fetch (race with
  //    AuthContext hydration). Render the outlet; the next render
  //    tick will re-evaluate once `account` resolves.
  if (loading || !account) {
    return <Outlet />;
  }

  // 2. Error — don't block on a failed `/api/account/me` fetch. The
  //    user can still navigate to `/settings/profile` via the
  //    user-pill menu and edit there.
  if (error) {
    return <Outlet />;
  }

  // 3. Dismissed for this account-on-this-browser — bypass.
  if (readDisplayNameDismissed(accountId)) {
    return <Outlet />;
  }

  // 4. Already on the profile page (with or without ?onboarding=1) —
  //    bypass to avoid a redirect loop. The destination IS the
  //    profile page; the gate must not bounce the user off it.
  if (location.pathname.startsWith("/settings/profile")) {
    return <Outlet />;
  }

  // 5. Real name set — bypass. The fallback heuristic returns false
  //    for `null`/empty too, which means a temporarily-empty
  //    display_name (mid-mutation) won't trigger a redirect. That's
  //    correct: only the exact `User <8-char>` cascade-step-6 shape
  //    deserves the gate.
  if (!isDisplayNameFallback(account.display_name)) {
    return <Outlet />;
  }

  // Otherwise — redirect to the onboarding variant of the profile
  // page. `replace` so the bounce doesn't pollute history.
  return <Navigate to="/settings/profile?onboarding=1" replace />;
}
